import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { PenTool } from 'lucide-react';
import { IconAlert } from '@documents-ui';
import { StaffPage } from '../../components/staff/StaffChrome';
import LegalNav from './LegalNav';

/**
 * LEGAL / SIGNATURES — not built, and this page says so without wasting the
 * reader's time.
 *
 * There is no envelope store yet (Task C3), so there is nothing to list and
 * this page makes NO requests. An empty table would read as "broken"; a count
 * of 0 would read as "nobody has signed". Instead it documents what a
 * signature record will actually contain, because the guarantees are the point
 * of the feature — anyone who has to defend one of these signatures needs to
 * know what the system captured and in what order.
 *
 * Every field and timestamp named below is already pinned by a schema in
 * `packages/shared-types/src/documentSigning.ts`; this page is the prose
 * version of that file, not a wish list. Replace it with the real envelope
 * list once C3 is deployed.
 */

/** Kept in step with `SignatureRecordSchema` / `AuditCertificateSchema`. */
const RECORD_FIELDS: { term: string; detail: string }[] = [
  {
    term: 'presentedSha256',
    detail:
      'The SHA-256 of the exact artifact the signer was shown — not of a document re-rendered '
      + 'afterwards. The order is fixed: render, hash, serve THAT artifact, capture intent, store '
      + 'the signature against the hash the signer saw. Hashing after the fact would leave room '
      + 'for a bug to substitute a different document in between. A signature record without this '
      + 'hash does not validate, because without it nobody can prove what was signed.',
  },
  {
    term: 'identityMethod',
    detail:
      'How the signer was identified: a Cognito session (tenant admins), a POS token (employees), '
      + 'an order reference or a loyalty account (customers). Email one-time codes are defined in '
      + 'the schema but nothing implements them — declared now so adding an outside party later is '
      + 'configuration rather than a migration of every existing record.',
  },
  {
    term: 'ip · userAgent',
    detail:
      'Evidence of the session that signed. At least one is required and blank strings are not '
      + 'evidence — a record whose only evidence is whitespace is refused, the same trim-aware rule '
      + 'the acceptance ledger already uses.',
  },
  {
    term: 'kind · signature image',
    detail:
      'Drawn or typed. The two are legally equivalent and the schema imposes identical '
      + 'requirements on both. The image evidences intent, not identity — the session, the '
      + 'timestamps and the presented hash are what prove who signed.',
  },
];

/** Spec §6.5 — the four timestamps the audit certificate lists per signer. */
const TIMESTAMPS: { field: string; label: string; why: string }[] = [
  { field: 'openedAt', label: 'Opened', why: 'the signer reached the document' },
  { field: 'consentAcceptedAt', label: 'E-consent accepted', why: 'they agreed to sign electronically, against a versioned disclosure' },
  { field: 'scrolledToEndAt', label: 'Scrolled to end', why: 'the whole agreement was actually put in front of them' },
  { field: 'signedAt', label: 'Signed', why: 'intent was captured' },
];

/* ── RUBY light-chrome primitives (local restyle of the dark @documents-ui
      primitives, which also render in platform-app and must stay dark). ── */

const BTN_BASE = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border px-4 text-[13px] transition-colors disabled:cursor-not-allowed disabled:opacity-50';
const BTN = {
  default: `${BTN_BASE} border-stone-300 bg-white text-stone-700 hover:bg-stone-50`,
  primary: `${BTN_BASE} border-transparent bg-admin-primary font-semibold text-white hover:bg-admin-secondary`,
};

const LIGHT_TAG_TONES: Record<string, string> = {
  draft: 'bg-stone-100 text-stone-600',
  retired: 'bg-red-100 text-red-800',
};

function LightTag({ tone, children }: { tone: string; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-semibold tracking-wide ${LIGHT_TAG_TONES[tone] ?? LIGHT_TAG_TONES.draft}`}>
      {children}
    </span>
  );
}

function LightWarn({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-800">
      <IconAlert size={14} className="mt-0.5 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

function LightDanger({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-xs leading-relaxed text-red-700">
      <IconAlert size={14} className="mt-0.5 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

export default function SignaturesPage() {
  const navigate = useNavigate();

  return (
    <StaffPage
      section="Legal"
      tabs={<LegalNav />}
      icon={PenTool}
      title="Signatures"
      subtitle="Executed envelopes, their signers, and the audit trail behind each signature."
      actions={(
        <button type="button" className={BTN.default} onClick={() => navigate('/legal/agreements')}>
          Agreements
        </button>
      )}
    >
      <div className="mx-auto max-w-3xl space-y-4">
        <LightWarn>
          <b>Nothing has been signed, because nothing can be yet.</b> A signature belongs to an
          <b> envelope</b> — one instance of a published agreement version addressed to named
          signers — and envelopes are still being built. This page makes no requests and shows no
          counts: a 0 here would claim that signing works and nobody has used it. What follows is
          what a signature record will contain, so that the guarantee is legible before the first
          one exists.
        </LightWarn>

        <section className="rounded-xl border border-stone-200 bg-white shadow-sm">
          <h3 className="border-b border-stone-200 bg-stone-50 px-4 py-3 text-[12px] font-semibold uppercase tracking-wider text-stone-500 rounded-t-xl">
            What one signature record holds
          </h3>
          <dl className="divide-y divide-stone-200">
            {RECORD_FIELDS.map((f) => (
              <div key={f.term} className="px-4 py-3.5">
                <dt className="font-mono text-[12px] font-semibold text-emerald-700">{f.term}</dt>
                <dd className="mt-1 text-[12.5px] leading-relaxed text-stone-600">{f.detail}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="rounded-xl border border-stone-200 bg-white shadow-sm">
          <h3 className="border-b border-stone-200 bg-stone-50 px-4 py-3 text-[12px] font-semibold uppercase tracking-wider text-stone-500 rounded-t-xl">
            The four timestamps, per signer
          </h3>
          <ol className="space-y-3 p-4">
            {TIMESTAMPS.map((t, i) => (
              <li key={t.field} className="flex gap-3">
                <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-[10.5px] font-bold text-emerald-700">
                  {i + 1}
                </span>
                <div className="min-w-0 text-[12.5px] leading-relaxed">
                  <span className="font-semibold text-stone-900">{t.label}</span>
                  <span className="ml-2 font-mono text-[11px] text-stone-500">{t.field}</span>
                  <div className="text-stone-600">{t.why}</div>
                </div>
              </li>
            ))}
          </ol>
          <p className="border-t border-stone-200 px-4 py-3 text-[12px] leading-relaxed text-stone-500">
            All four are stamped in UTC and copied into the audit certificate alongside the
            presented and executed hashes and the renderer version, so a third party can re-render
            the agreement and check the digests without access to this system.
          </p>
        </section>

        <section className="rounded-xl border border-stone-200 bg-white shadow-sm">
          <h3 className="border-b border-stone-200 bg-stone-50 px-4 py-3 text-[12px] font-semibold uppercase tracking-wider text-stone-500 rounded-t-xl">
            Where signing happens
          </h3>
          <ul className="divide-y divide-stone-200">
            <li className="flex flex-wrap items-center gap-2 px-4 py-3 text-[12.5px] text-stone-600">
              <span className="font-semibold text-stone-900">Tenant admins</span>
              <span className="text-stone-500">· platform-app, Cognito session</span>
              <LightTag tone="draft">not built</LightTag>
            </li>
            <li className="flex flex-wrap items-center gap-2 px-4 py-3 text-[12.5px] text-stone-600">
              <span className="font-semibold text-stone-900">Employees</span>
              <span className="text-stone-500">· POS terminal, PIN-issued token</span>
              <LightTag tone="retired">blocked — key rotation</LightTag>
            </li>
            <li className="flex flex-wrap items-center gap-2 px-4 py-3 text-[12.5px] text-stone-600">
              <span className="font-semibold text-stone-900">Customers</span>
              <span className="text-stone-500">· customer display, order or loyalty reference</span>
              <LightTag tone="draft">not built</LightTag>
            </li>
          </ul>
        </section>

        <LightDanger>
          <b>Employee signing on POS terminals is gated on a pending key rotation.</b> Production
          still signs POS tokens with a secret that is published in the repository, so a token —
          and therefore an employee identity — can be forged. Until it is rotated, the service
          refuses to create an employee signature record at all and answers{' '}
          <span className="font-mono">503 employee_signing_locked_pending_key_rotation</span>.
          Employee documents still render, read and can be acknowledged; only the production of a
          signature is blocked. The gate checks the current and previous secrets as a pair, so it
          stays shut for the whole rotation window rather than lifting the moment rotation starts,
          and it lifts by itself when the secret is healthy — there is no flag for anyone to
          switch off.
        </LightDanger>

        <div className="rounded-xl border border-stone-200 bg-white p-4 text-[12.5px] leading-relaxed text-stone-600 shadow-sm">
          <b className="text-stone-900">One rule worth knowing before the first signature:</b>{' '}
          execution is terminal. Once every signer has signed, an envelope can never be voided or
          declined — an executed agreement is superseded by another agreement, not unwound. Void
          exists for envelopes that were sent in error, and it always demands a reason.
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" className={BTN.primary} onClick={() => navigate('/legal/agreements')}>
            Go to Agreements
          </button>
          <button type="button" className={BTN.default} onClick={() => navigate('/legal/policies')}>Policies</button>
        </div>
      </div>
    </StaffPage>
  );
}
